// FLOW — "clear my book" mode. One relationship at a time, full screen, keyboard first:
//   1 / 2 / 3  log the conversation (positive · neutral · no answer) → next
//   Enter      the suggested move (complete task, open case, book review…)
//   N note · S tomorrow (snooze) · → skip · Esc exit
// The queue is fixed when Flow starts (so it doesn't reshuffle under your fingers) and each card is
// re-read live, so anything cleared elsewhere simply disappears from the queue.
import { useEffect, useMemo, useRef, useState } from 'react';
import { attentionByPerson, lastContact, personPulse, relationshipHealth, type AttentionItem } from '@vero/intelligence';
import { logContact, completeTask, diffDays, type Outcome } from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Avatar, reasonText } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { Band } from '../core/viz.tsx';
import { telLink, waLink } from '../core/links.ts';
import { navigate, personPath } from '../core/router.ts';
import { performAction } from './attention.tsx';
import { openers } from './forms.tsx';

export function startFlow(rt: Runtime) { rt.openLayer(<FlowLayer key={Date.now()} />); }

/** One entry per person/prospect, most urgent first (moments of low severity excluded). */
export function flowQueue(items: AttentionItem[]): string[] {
  const groups = [...attentionByPerson(items.filter((a) => !((a.rule === 'birthday' || a.rule === 'nameday') && a.severity === 'low'))).values()];
  return groups.sort((a, b) => b[0]!.score - a[0]!.score).map((g) => g[0]!.personId ?? g[0]!.prospectId ?? g[0]!.id);
}
const keyOf = (a: AttentionItem) => a.personId ?? a.prospectId ?? a.id;

function FlowLayer() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const [queue] = useState(() => flowQueue(rt.attention));
  const [pos, setPos] = useState(0);
  const [cleared, setCleared] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const busy = useRef(false);

  // live items of the current relationship; skip entries that were resolved meanwhile
  const liveByKey = useMemo(() => {
    const m = new Map<string, AttentionItem[]>();
    for (const a of rt.attention) { const k = keyOf(a); m.set(k, [...(m.get(k) ?? []), a]); }
    return m;
  }, [rt.attention]);
  useEffect(() => {
    let p = pos;
    while (p < queue.length && !liveByKey.get(queue[p]!)?.length) p++;
    if (p !== pos) setPos(p);
  }, [pos, queue, liveByKey]);

  const key = queue[pos];
  const items = key ? (liveByKey.get(key) ?? []).sort((a, b) => b.score - a.score) : [];
  const lead = items[0];
  const person = lead?.personId ? view.get('persons', lead.personId) : undefined;
  const prospect = lead?.prospectId ? view.get('prospects', lead.prospectId) : undefined;
  const name = person?.displayName ?? prospect?.name ?? '';
  const done = pos >= queue.length;

  const next = (didClear: boolean) => {
    setLeaving(true);
    setTimeout(() => { setLeaving(false); if (didClear) setCleared((c) => c + 1); setPos((p) => p + 1); busy.current = false; }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 240);
  };
  const exit = () => rt.closeLayer();

  const outcome = async (o: Outcome) => {
    if (!lead || busy.current) return;
    busy.current = true;
    if (lead.action.kind === 'complete_task') {
      const id = lead.action.taskId;
      if (await rt.run((v, c) => completeTask(v, id, { outcome: o }, c), 'flow.logged')) return next(true);
    } else if (person) {
      // one atomic change: the logged call + snoozing this item for today (the underlying need, e.g. a
      // review, stays visible elsewhere) — so a single Undo reverts both
      const snoozeCmd = rt.snoozeCommand(lead.id);
      const title = t(`flow.outcome_title.${o}`, { reason: reasonText(i18n, lead) });
      if (await rt.run((v, c) => { const a = logContact(v, { personId: person.id, kind: 'call', outcome: o, title }, c); const b = snoozeCmd(v, c) as typeof a; return { ...a, puts: [...a.puts, ...b.puts] }; }, 'flow.logged')) return next(true);
    } else { await rt.snooze(lead.id); return next(true); }
    busy.current = false;
  };
  const primary = () => {
    if (!lead) return;
    const nav = lead.actionKey !== 'attention.action.prepare_meeting' && (lead.action.kind === 'open_case' || lead.action.kind === 'open_person' || (lead.action.kind === 'call' && !!lead.action.prospectId));
    if (nav) exit();
    performAction(rt, lead);
  };
  const snooze = async () => { if (!lead || busy.current) return; busy.current = true; await rt.snooze(lead.id); next(true); };
  const skip = () => { if (busy.current) return; busy.current = true; next(false); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('.overlay')) return; // a form is open on top
      const el = e.target as HTMLElement | null;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName ?? '')) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') { e.preventDefault(); exit(); return; }
      if (done) { if (e.key === 'Enter') exit(); return; }
      if (e.key === '1') void outcome('positive');
      else if (e.key === '2') void outcome('neutral');
      else if (e.key === '3') void outcome('negative');
      else if (e.key === 'Enter') { e.preventDefault(); primary(); }
      else if (e.key.toLowerCase() === 'n' && person) { e.preventDefault(); openers(rt).note(person.id); }
      else if (e.key.toLowerCase() === 's') void snooze();
      else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') skip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const pct = queue.length ? Math.round((Math.min(pos, queue.length) / queue.length) * 100) : 100;
  const pulse = person ? personPulse(view, person.id, today) : null;
  const health = person && person.stage === 'client' ? relationshipHealth(view, person.id, today) : null;
  const lc = person ? lastContact(view, person.id) : null;
  const phone = person?.primaryPhone ?? prospect?.phone;

  return (
    <div className="flow-layer" role="dialog" aria-modal="true" aria-label={t('flow.title')} data-testid="flow">
      <div className="flow-top">
        <h2 className="grad-text">{t('flow.title')}</h2>
        <div className="flow-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>
        <span className="small muted num" data-testid="flow-progress">{t('flow.progress', { done: Math.min(pos, queue.length), total: queue.length })}</span>
        <button className="btn btn-ghost btn-sm" onClick={exit} data-testid="flow-exit">{t('flow.exit')}<span className="kbd">Esc</span></button>
      </div>

      <div className="flow-stage">
        {done || !lead ? (
          <div className="flow-done" data-testid="flow-done">
            <div className="orb" aria-hidden="true" />
            <h3>{queue.length ? t('flow.done_title') : t('flow.empty')}</h3>
            {queue.length > 0 && <p className="muted" style={{ margin: 0 }}>{t('flow.done_body', { count: cleared })}</p>}
            <button className="btn btn-primary btn-lg" onClick={exit}>{t('common.done')}</button>
          </div>
        ) : (
          <article key={key} className={`flow-card card${leaving ? ' leaving' : ''}`} data-testid="flow-card">
            <div className="flow-person">
              <Avatar name={name} size="lg" ring={lead.severity} />
              <div style={{ minWidth: 0 }} className="grow">
                <div className="row wrap" style={{ gap: 8, marginBottom: 4 }}>
                  <span className={`chip ${lead.severity === 'critical' ? 'crit' : lead.severity === 'high' ? 'high' : lead.severity === 'medium' ? 'gold' : 'low'}`}>{i18n.enumLabel('severity', lead.severity)}</span>
                  {person && <span className="chip">{i18n.enumLabel('stage', person.stage)}</span>}
                  {prospect && <span className="chip">{i18n.enumLabel('stage', 'prospect')}</span>}
                  {health && <Band band={health.band} label={`${t(`health.band.${health.band}`)} · ${health.score}`} />}
                </div>
                <h3 className="person-name" data-testid="flow-name">{name}</h3>
              </div>
              {person && <button className="btn btn-ghost btn-sm" onClick={() => { exit(); navigate(personPath(person.id)); }}>{t('attention.action.open')}<Icon name="arrow" size={14} /></button>}
            </div>
            <p className="flow-reason" data-testid="flow-reason">{reasonText(i18n, lead)}</p>
            {items.length > 1 && <div className="flow-also">{items.slice(1, 5).map((x) => <span key={x.id} className="chip">{reasonText(i18n, x)}</span>)}</div>}
            {person && pulse && (
              <div className="flow-ctx">
                <div><span>{t('flow.last_contact')}</span><b>{lc ? `${i18n.date(lc.at, 'dayMonth')} · ${t('flow.days_ago', { days: diffDays(lc.at.slice(0, 10), today) })}` : t('brief.never_contacted')}</b></div>
                <div><span>{t('flow.portfolio')}</span><b className="num">{i18n.money(pulse.annualPortfolio)}</b></div>
                <div><span>{t('flow.open_cases')}</span><b className="num">{pulse.activeCases}</b></div>
              </div>
            )}
            <div className="flow-actions">
              <button className="btn btn-primary btn-lg" onClick={primary} data-testid="flow-primary">{t(lead.actionKey)}<span className="kbd" style={{ background: 'transparent', borderColor: 'currentColor', color: 'inherit' }}>↵</span></button>
              {phone && <a className="btn btn-lg" href={telLink(phone)}><Icon name="phone" />{phone}</a>}
              {phone && <a className="btn btn-lg btn-icon" style={{ width: 44 }} href={waLink(phone)} target="_blank" rel="noreferrer" aria-label={t('common.whatsapp')}><Icon name="chat" /></a>}
              <span className="grow" />
              {person && <button className="btn btn-ghost" onClick={() => openers(rt).note(person.id)}><Icon name="note" />{t('flow.note')}<span className="kbd">N</span></button>}
              <button className="btn btn-ghost" onClick={() => void snooze()} data-testid="flow-snooze">{t('flow.snooze')}<span className="kbd">S</span></button>
              <button className="btn btn-ghost" onClick={skip} data-testid="flow-skip">{t('flow.skip')}<span className="kbd">→</span></button>
            </div>
            <div className="upper" style={{ marginTop: 20 }}>{t('flow.outcome_q')}</div>
            <div className="flow-outcomes">
              <button className="btn" onClick={() => void outcome('positive')} data-testid="flow-outcome-1"><span className="kbd">1</span>{t('flow.outcome.positive')}</button>
              <button className="btn" onClick={() => void outcome('neutral')}><span className="kbd">2</span>{t('flow.outcome.neutral')}</button>
              <button className="btn" onClick={() => void outcome('negative')}><span className="kbd">3</span>{t('flow.outcome.negative')}</button>
            </div>
          </article>
        )}
      </div>
      <div className="flow-keys" aria-hidden="true">
        <span><span className="kbd">1</span><span className="kbd">2</span><span className="kbd">3</span>{t('flow.keys_log')}</span>
        <span><span className="kbd">↵</span>{t('flow.keys_move')}</span>
        <span><span className="kbd">N</span>{t('flow.note')}</span>
        <span><span className="kbd">S</span>{t('flow.snooze')}</span>
        <span><span className="kbd">→</span>{t('flow.skip')}</span>
        <span><span className="kbd">Esc</span>{t('flow.exit')}</span>
      </div>
    </div>
  );
}
